import React, { useEffect, useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/ui/card.jsx";
import axios from "../../lib/axios.js";
import { Skeleton } from "../../components/ui/skeleton.jsx";
import {
    startOfMonth,
    endOfMonth,
    subDays,
    subMonths,
    format,
    differenceInDays,
} from "date-fns";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { useToast } from "@/hooks/use-toast.js";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, } from "@/components/ui/tooltip"
import MinimalPdfGenerator from "./PDF.jsx";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";

const CallAggregatedReports = ({ community }) => {
    const { id: communityId } = community;
    const [aggregatedCallData, setAggregatedCallData] = useState({
        total_calls: 0,
        total_incoming_calls: 0,
        total_outgoing_calls: 0,
        total_outgoing_community_answered: 0,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [preset, setPreset] = useState("Last 30 Days");
    const { toast } = useToast();

    const [dateRange, setDateRange] = useState({
        from: subDays(new Date(), 29), // Default: Last 30 days
        to: new Date(),
    });

    const communityAnswerRate =
        parseInt(aggregatedCallData.total_outgoing_calls) === 0
            ? 0
            : (aggregatedCallData.total_outgoing_community_answered / aggregatedCallData.total_outgoing_calls) * 100;

    const validateDateRange = (range) => {
        if (!range.from || !range.to) {
            return false;
        }
        const daysDiff = differenceInDays(range.to, range.from) + 1;
        if (daysDiff < 1) {
            return false;
        }
        if (daysDiff > 31) {
            return false;
        }
        return true;
    };

    const fetchAllData = async () => {
        if (!validateDateRange(dateRange)) {
            return;
        }

        const adjustedDateRange = {
            from: new Date(dateRange.from.setHours(0, 0, 0, 0)), // Start of the day
            to: new Date(dateRange.to.setHours(23, 59, 59, 999)), // End of the day
        };

        setIsLoading(true);

        try {
            const params = {
                start_date: adjustedDateRange.from.toISOString().split("T")[0],
                end_date: adjustedDateRange.to.toISOString().split("T")[0],
                community_id: communityId,
            };

            const encodedParams = new URLSearchParams(params).toString();

            const response = await axios.get(
                `/dashboard/calls-aggregate?${encodedParams}`
            );

            setAggregatedCallData(response.data)
        } catch (error) {
            console.error("Error fetching total calls:", error);
            toast({
                title: "Error",
                description: "Error fetching aggregated call data.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, [dateRange]);

    const handlePresetSelection = (preset) => {
        const today = new Date();
        switch (preset) {
            case "Last 7 Days":
                setDateRange({ from: subDays(today, 6), to: today });
                setPreset("Last 7 Days");
                break;
            case "Last 30 Days":
                setDateRange({ from: subDays(today, 29), to: today });
                setPreset("Last 30 Days");
                break;
            case "Last Month":
                const lastMonthStart = startOfMonth(subMonths(today, 1));
                const lastMonthEnd = endOfMonth(subMonths(today, 1));
                setDateRange({ from: lastMonthStart, to: lastMonthEnd });
                setPreset("Last Month");
                break;
            case "This Month":
                const thisMonthStart = startOfMonth(today);
                const thisMonthEnd = today;
                setDateRange({ from: thisMonthStart, to: thisMonthEnd });
                setPreset("This Month");
                break;
            default:
                break;
        }
    };

    const SkeletonCard = () => (
        <Card className="bg-gray-50 border-none">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-4 rounded-full" />
            </CardHeader>
            <CardContent>
                <Skeleton className="h-8 w-24" />
            </CardContent>
        </Card>
    );

    const cards = [
        {
            title: "Total Calls",
            value: aggregatedCallData.total_calls,
            description: 'Total calls made either from Flair or recieved from callbacks.'
        },
        {
            title: "Outgoing",
            value: aggregatedCallData.total_outgoing_calls,
            description: 'Total calls both made from Flair reaching out to community.'
        },
        {
            title: "Incoming",
            value: aggregatedCallData.total_incoming_calls,
            description: 'Total calls recieved from callbacks.'
        },
        {
            title: "Answered Outgoing",
            value: aggregatedCallData.total_outgoing_community_answered,
            description: 'Total calls where the community picked up the phone and pressed 1.'
        },
        {
            title: "Answer Rate",
            value: `${parseFloat(communityAnswerRate).toFixed(2)}%`,
            description: 'Leasing Team Answer Rate: The percentage of outgoing calls that were answered by the community'
        },
    ];
    const presetOptions = [
        "Last 7 Days",
        "Last 30 Days",
        "Last Month",
        "This Month",
    ];

    return (
        <TooltipProvider>
            <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                    <h2 className="text-gray-500 text-xl font-medium">Call Analytics</h2>
                    <div className="flex flex-row gap-2">
                        <MinimalPdfGenerator community={community} dateRange={dateRange} />
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" className="w-[auto] justify-start">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {preset === "Custom Range" ? (
                                        <>
                                            {dateRange.from && dateRange.to
                                                ? `${format(dateRange.from, "MMM dd, yyyy")} - ${format(
                                                    dateRange.to,
                                                    "MMM dd, yyyy"
                                                )}`
                                                : "Pick a date range"}
                                        </>
                                    ) : (
                                        <>{preset}</>
                                    )}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto flex flex-col items-center p-4" align="end">
                                <div className="flex flex-wrap justify-center gap-2 mb-4">
                                    {presetOptions.map((option) => (
                                        <Button
                                            key={option}
                                            variant={preset === option ? "secondary" : "outline"}
                                            onClick={() => handlePresetSelection(option)}
                                        >
                                            {option}
                                        </Button>
                                    ))}
                                </div>
                                <Calendar
                                    mode="range"
                                    max={31}
                                    numberOfMonths={2}
                                    selected={dateRange}
                                    onSelect={(range) => {
                                        setDateRange({ from: range?.from, to: range?.to });
                                        setPreset("Custom Range");
                                    }}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                    {isLoading
                        ? Array(5)
                            .fill(0)
                            .map((_, index) => <SkeletonCard key={`skeleton-${index}`} />)
                        : cards.map((card, index) => (
                            <Card
                                key={index}
                                className="border-none relative"
                            >

                                <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                                    <CardTitle className="w-full truncate text-sm font-light text-gray-600 flex justify-between">
                                        <span>{card.title}</span>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="cursor-pointer">
                                                    <QuestionMarkCircledIcon className="h-4 w-4 text-gray-500" />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                {card.description}
                                            </TooltipContent>
                                        </Tooltip>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-2xl md:text-3xl font-bold">
                                        {card.value}
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                </div>
            </div>
        </TooltipProvider>
    );
};

export default CallAggregatedReports;
